// Servers — the detail behind a Device Config whose type is SERVER.
// Specs, RAM slots, disk bays, applications and credentials.
//
// A password is never in any payload this page receives. Seeing one takes a
// code emailed to the person asking, and the reveal is logged.
import React, { useState, useEffect, useCallback } from 'react';
import api from '../api';
import { useAuth } from '../context/AuthContext';

const errText = (e, fallback = 'Something went wrong') => {
  const d = e?.response?.data;
  const pick = v => !v ? '' : typeof v === 'string' ? v : (v.message || v.error || '');
  return pick(d?.error) || pick(d) || e?.message || fallback;
};
const dt = v => v ? new Date(v).toLocaleString('en-IN', { day:'2-digit', month:'short', hour:'2-digit', minute:'2-digit' }) : '';

export default function Servers() {
  const { can } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sel, setSel] = useState(null);
  const [f, setF] = useState({ search:'', app:'' });

  const load = useCallback(() => {
    setLoading(true);
    const params = {}; Object.entries(f).forEach(([k,v]) => { if (v) params[k]=v; });
    api.get('/servers', { params }).then(r => setRows(r.data)).catch(()=>{}).finally(()=>setLoading(false));
  }, [f]);
  useEffect(() => { load(); }, [load]);

  if (sel) return <Detail id={sel} onBack={() => { setSel(null); load(); }} can={can} />;

  return (
    <div>
      <div style={{ marginBottom:14 }}>
        <h2 style={{ fontSize:18, fontWeight:600, marginBottom:4 }}>Servers</h2>
        <p style={{ fontSize:13, color:'var(--text2)' }}>
          Every campus server — CPU, memory and disk slots, operating system, applications and credentials.
        </p>
      </div>

      <div className="card" style={{ marginBottom:14, padding:12 }}>
        <div style={{ display:'grid', gridTemplateColumns:'2fr 1.5fr auto', gap:10, alignItems:'end' }}>
          <div><label style={{ fontSize:11, color:'var(--text2)' }}>Search name, IP, CPU or OS</label>
            <input value={f.search} onChange={e => setF(s=>({...s,search:e.target.value}))} /></div>
          <div><label style={{ fontSize:11, color:'var(--text2)' }}>Running application</label>
            <input value={f.app} onChange={e => setF(s=>({...s,app:e.target.value}))} placeholder="e.g. SQL Server" /></div>
          <button className="btn btn-secondary" onClick={load}>Refresh</button>
        </div>
      </div>

      <div className="card" style={{ padding:0, overflow:'hidden' }}>
        {loading ? <div style={{ padding:40, textAlign:'center', color:'var(--text2)' }}>Loading…</div>
        : !rows.length ? <div className="empty-state"><p>
            No servers yet. Add the machine in <b>Assets</b> with the type SERVER — it will appear
            here automatically, ready for its software, IP and logins.</p></div>
        : <div className="table-wrap"><table>
            <thead><tr><th>Server</th><th>Campus</th><th>CPU</th><th>RAM</th><th>Disk</th>
              <th>LAN</th><th>OS</th><th>Apps</th><th>Logins</th></tr></thead>
            <tbody>{rows.map(s => (
              <tr key={s.asset_id} style={{ cursor:'pointer' }} onClick={() => setSel(s.asset_id)}>
                <td style={{ fontSize:13 }}><b>{s.device_name}</b>
                  <div className="mono" style={{ fontSize:11.5, color:'var(--text2)' }}>
                    {s.ip_address || <span style={{ color:'var(--text3)' }}>no IP yet</span>}</div>
                  {/* A server that is in the asset register but has nothing
                      operational recorded is the one worth chasing. */}
                  {s.needs_setup && <span className="badge badge-high" style={{ fontSize:10 }}>needs setup</span>}</td>
                <td style={{ fontSize:12.5 }}>{s.campus_name}</td>
                <td style={{ fontSize:12 }}>{s.cpu_model || '—'}</td>
                <td style={{ fontSize:12 }}>{s.ram_gb ? `${s.ram_gb} GB` : '—'}
                  {s.ram_slots_total != null &&
                    <div style={{ color: s.ram_used < s.ram_slots_total ? 'var(--green)' : 'var(--text2)' }}>
                      {s.ram_used}/{s.ram_slots_total} slots
                      {s.ram_used < s.ram_slots_total && ` · ${s.ram_slots_total - s.ram_used} free`}
                    </div>}</td>
                <td style={{ fontSize:12 }}>{s.disk_gb ? `${s.disk_gb} GB` : '—'}
                  {s.bays_total != null &&
                    <div style={{ color: s.bays_used < s.bays_total ? 'var(--green)' : 'var(--text2)' }}>
                      {s.bays_used}/{s.bays_total} bays
                      {s.bays_used < s.bays_total && ` · ${s.bays_total - s.bays_used} free`}
                    </div>}</td>
                {/* The dual-NIC machines are the ones worth spotting at a glance. */}
                <td>{s.lan_ports > 1
                  ? <span className="badge badge-completed">{s.lan_ports} ports</span>
                  : <span style={{ fontSize:12, color:'var(--text2)' }}>{s.lan_ports || 1}</span>}</td>
                <td style={{ fontSize:12 }}>{s.os_name || '—'}
                  {s.os_version && <div style={{ color:'var(--text2)' }}>{s.os_version}</div>}</td>
                <td style={{ fontSize:12 }}>{s.app_count || 0}</td>
                <td style={{ fontSize:12 }}>{s.cred_count ? `🔒 ${s.cred_count}` : '—'}</td>
              </tr>
            ))}</tbody>
          </table></div>}
      </div>
    </div>
  );
}

// ── Detail ────────────────────────────────────────────────────────────────
function Detail({ id, onBack, can }) {
  const [d, setD] = useState(null);
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);
  const [spec, setSpec] = useState(null);

  const load = useCallback(() => api.get(`/servers/${id}`).then(r => {
    setD(r.data);
    // Hostname and IP live on the config row, not in details — seed them so
    // the form shows what is stored rather than blanks.
    setSpec({ ...r.data.details, device_name: r.data.device_name || '', ip_address: r.data.ip_address || '' });
  }).catch(e => setMsg(errText(e))), [id]);
  useEffect(() => { load(); }, [load]);
  if (!d || !spec) return <div style={{ padding:40, color:'var(--text2)' }}>Loading…</div>;

  const editable = can('configs','edit');
  const set = (k,v) => setSpec(p => ({ ...p, [k]:v }));
  const act = async (fn, ok) => {
    setBusy(true); setMsg('');
    try { await fn(); setMsg(ok || '✓ Saved'); load(); }
    catch (e) { setMsg(errText(e)); }
    finally { setBusy(false); }
  };

  const field = (k, label, type = 'text', width) => (
    <div className="form-group" style={{ margin:0, width }}>
      <label>{label}</label>
      <input type={type} value={spec[k] ?? ''} disabled={!editable}
             onChange={e => set(k, e.target.value)} />
    </div>
  );

  return (
    <div>
      <button className="btn btn-secondary" style={{ marginBottom:12 }} onClick={onBack}>← All servers</button>

      <div className="card" style={{ marginBottom:14 }}>
        <h2 style={{ fontSize:19, fontWeight:600, marginBottom:2 }}>{d.device_name}</h2>
        <div style={{ fontSize:13, color:'var(--text2)' }}>
          {d.campus_name}{d.room_name ? ` · ${d.room_name}` : ''}{d.location ? ` · ${d.location}` : ''}
        </div>
        {msg && <div style={{ marginTop:8, fontSize:13, color: msg.startsWith('✓')?'var(--green)':'var(--red)' }}>{msg}</div>}
      </div>

      {/* Hardware comes from the asset register and is not editable here —
          a serial number is entered once, in Assets. */}
      <div className="card" style={{ marginBottom:14, background:'var(--bg3)' }}>
        <div className="flex-gap" style={{ justifyContent:'space-between', alignItems:'center', flexWrap:'wrap', gap:8 }}>
          <b style={{ fontSize:14 }}>Hardware <span style={{ fontWeight:400, fontSize:12, color:'var(--text2)' }}>— from the asset record</span></b>
          <span className="mono badge badge-normal">{d.asset_tag}</span>
        </div>
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(150px, 1fr))', gap:'6px 16px', marginTop:10 }}>
          {[['Make', d.brand], ['Model', d.model], ['Serial', d.serial_number],
            ['MAC (NIC 1)', d.mac_address], ['Condition', d.condition], ['Status', d.asset_status],
            ['Purchased', d.purchase_date && new Date(d.purchase_date).toLocaleDateString('en-IN')],
            ['Warranty ends', d.warranty_expiry && new Date(d.warranty_expiry).toLocaleDateString('en-IN')],
          ].map(([k, v]) => (
            <div key={k}>
              <div style={{ fontSize:11, color:'var(--text2)' }}>{k}</div>
              <div style={{ fontSize:13 }} className={k.startsWith('MAC') || k === 'Serial' ? 'mono' : ''}>
                {v || <span style={{ color:'var(--text3)' }}>—</span>}</div>
            </div>
          ))}
        </div>
        <div style={{ fontSize:11.5, color:'var(--text2)', marginTop:10 }}>
          Change any of these in <b>Assets</b> — they are the asset's facts, not the server's.
        </div>
      </div>

      {/* Specs */}
      <div className="card" style={{ marginBottom:14 }}>
        <b style={{ fontSize:14 }}>Software &amp; network</b>
        <div style={{ fontSize:12, color:'var(--text2)', marginTop:3 }}>What this machine runs and how it is reached — the operational half.</div>
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(170px, 1fr))', gap:10, marginTop:10 }}>
          <div className="form-group" style={{ margin:0 }}><label>Hostname</label>
            <input value={spec.device_name ?? d.device_name ?? ''} disabled={!editable}
                   onChange={e => set('device_name', e.target.value)} /></div>
          <div className="form-group" style={{ margin:0 }}><label>IP address</label>
            <input value={spec.ip_address ?? d.ip_address ?? ''} disabled={!editable}
                   onChange={e => set('ip_address', e.target.value)} placeholder="10.3.0.10" /></div>
          {field('cpu_model','CPU model')}
          {field('cpu_sockets','Sockets','number')}
          {field('cpu_cores','Cores','number')}
          {field('cpu_threads','Threads','number')}
          {field('cpu_base_ghz','Base GHz')}
          {field('ram_slots_total','RAM slots on board','number')}
          {field('ram_type','RAM type')}
          {field('bays_total','Drive bays','number')}
          {field('lan_ports','LAN ports','number')}
          {/* Only servers have a second NIC, so its MAC sits here rather than
              widening every asset row. */}
          {Number(spec.lan_ports) > 1 && field('nic2_mac','MAC (NIC 2)')}
          {field('os_name','Operating system')}
          {field('os_version','OS version')}
          {field('os_license','OS licence')}
        </div>
        <div className="form-group" style={{ marginTop:10 }}><label>Notes</label>
          <textarea rows={2} value={spec.notes || ''} disabled={!editable}
                    onChange={e => set('notes', e.target.value)} /></div>
        {editable && <button className="btn btn-primary" disabled={busy}
          onClick={() => act(() => api.put(`/servers/${id}`, spec))}>Save software & network</button>}
      </div>

      <RowEditor title="Memory" subtitle={
          d.details.ram_slots_total != null
            ? `${d.ram.length} of ${d.details.ram_slots_total} slots used — ${d.ram_free} free`
            : 'Set "RAM slots on board" above to track free slots'}
        rows={d.ram} editable={editable} busy={busy}
        cols={[['slot_no','Slot','number',70],['size_gb','Size GB','number',90],
               ['model','Model','text',null],['serial_number','Serial','text',null]]}
        blank={() => ({ slot_no:'', size_gb:'', model:'', serial_number:'' })}
        onSave={rows => act(() => api.put(`/servers/${id}/ram`, { rows }), '✓ Memory saved')} />

      <RowEditor title="Storage" subtitle={
          d.details.bays_total != null
            ? `${d.storage.length} of ${d.details.bays_total} bays used — ${d.bays_free} free`
            : 'Set "Drive bays" above to track free bays'}
        rows={d.storage} editable={editable} busy={busy}
        cols={[['bay_no','Bay','number',70],['kind','Type','select',90],
               ['size_gb','Size GB','number',90],['model','Model','text',null],['serial_number','Serial','text',null]]}
        options={{ kind:['HDD','SSD','NVMe'] }}
        blank={() => ({ bay_no:'', kind:'HDD', size_gb:'', model:'', serial_number:'' })}
        onSave={rows => act(() => api.put(`/servers/${id}/storage`, { rows }), '✓ Storage saved')} />

      <RowEditor title="Applications installed" subtitle="Listed as rows, so you can search which servers run something"
        rows={d.apps} editable={editable} busy={busy}
        cols={[['name','Application','text',null],['version','Version','text',110],['purpose','Purpose','text',null]]}
        blank={() => ({ name:'', version:'', purpose:'' })}
        onSave={rows => act(() => api.put(`/servers/${id}/apps`, { rows }), '✓ Applications saved')} />

      <Credentials server={d} id={id} editable={editable} reload={load} setMsg={setMsg} />
    </div>
  );
}

// A short list edited in place and saved whole — simpler than per-row ids for
// a handful of slots.
function RowEditor({ title, subtitle, rows, cols, blank, onSave, editable, busy, options = {} }) {
  const [list, setList] = useState(rows);
  useEffect(() => { setList(rows); }, [rows]);
  const upd = (i, k, v) => setList(l => l.map((r, j) => j === i ? { ...r, [k]:v } : r));

  return (
    <div className="card" style={{ marginBottom:14 }}>
      <b style={{ fontSize:14 }}>{title}</b>
      <div style={{ fontSize:12, color:'var(--text2)', margin:'3px 0 8px' }}>{subtitle}</div>
      {!list.length && <div style={{ fontSize:12.5, color:'var(--text2)' }}>Nothing recorded.</div>}
      {list.map((r, i) => (
        <div key={i} className="flex-gap" style={{ gap:8, marginBottom:6, alignItems:'center', flexWrap:'wrap' }}>
          {cols.map(([k, label, type, w]) => (
            type === 'select'
              ? <select key={k} value={r[k] || ''} disabled={!editable} style={{ width:w || undefined }}
                        onChange={e => upd(i, k, e.target.value)}>
                  {(options[k] || []).map(o => <option key={o} value={o}>{o}</option>)}
                </select>
              : <input key={k} type={type} value={r[k] ?? ''} placeholder={label} disabled={!editable}
                       style={{ width:w || undefined, flex: w ? undefined : 1, minWidth:110 }}
                       onChange={e => upd(i, k, e.target.value)} />
          ))}
          {editable && <button className="btn btn-secondary" style={{ padding:'2px 8px', fontSize:11 }}
            onClick={() => setList(l => l.filter((_, j) => j !== i))}>remove</button>}
        </div>
      ))}
      {editable && (
        <div className="flex-gap" style={{ gap:8, marginTop:8 }}>
          <button className="btn btn-secondary" onClick={() => setList(l => [...l, blank()])}>+ Add</button>
          <button className="btn btn-primary" disabled={busy} onClick={() => onSave(list)}>Save {title.toLowerCase()}</button>
        </div>
      )}
    </div>
  );
}

// ── Credentials ───────────────────────────────────────────────────────────
function Credentials({ server, id, editable, reload, setMsg }) {
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ username:'', purpose:'', password:'' });
  const [otpFor, setOtpFor] = useState(null);     // credential id awaiting a code
  const [otp, setOtp] = useState('');
  const [sentTo, setSentTo] = useState('');
  const [shown, setShown] = useState({});         // credential id -> password
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const save = async () => {
    setBusy(true); setErr('');
    try {
      await api.post(`/servers/${id}/credentials`, form);
      setForm({ username:'', purpose:'', password:'' }); setAdding(false); reload();
      setMsg('✓ Credential saved');
    } catch (e) { setErr(errText(e)); }
    finally { setBusy(false); }
  };

  const askCode = async (cid) => {
    setBusy(true); setErr(''); setOtp('');
    try {
      const r = await api.post(`/servers/${id}/credentials/${cid}/otp`);
      setOtpFor(cid); setSentTo(r.data.sent_to);
    } catch (e) { setErr(errText(e)); }
    finally { setBusy(false); }
  };

  const reveal = async (cid) => {
    setBusy(true); setErr('');
    try {
      const r = await api.post(`/servers/${id}/credentials/${cid}/reveal`, { otp });
      setShown(s => ({ ...s, [cid]: r.data.password }));
      setOtpFor(null); setOtp('');
      // Hide it again after a minute rather than leaving it on screen.
      setTimeout(() => setShown(s => { const n = { ...s }; delete n[cid]; return n; }), 60000);
    } catch (e) { setErr(errText(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="card" style={{ marginBottom:14 }}>
      <b style={{ fontSize:14 }}>Logins</b>
      <div style={{ fontSize:12, color:'var(--text2)', margin:'3px 0 8px' }}>
        Passwords are encrypted and never sent to this screen. Viewing one needs a code
        emailed to you, and every view is recorded.
      </div>

      {!server.crypto_ready && (
        <div style={{ padding:'8px 10px', borderRadius:6, marginBottom:10, fontSize:12.5,
          background:'rgba(220,70,70,.08)', border:'1px solid var(--red)' }}>
          <b style={{ color:'var(--red)' }}>No encryption key on the server.</b> Passwords cannot be
          stored until <span className="mono">CRED_KEY</span> is set in the server environment.
        </div>
      )}

      {!server.credentials.length && <div style={{ fontSize:12.5, color:'var(--text2)' }}>No logins recorded.</div>}
      {server.credentials.map(c => (
        <div key={c.id} style={{ padding:'8px 0', borderBottom:'1px solid var(--border)' }}>
          <div className="flex-gap" style={{ justifyContent:'space-between', alignItems:'center', flexWrap:'wrap', gap:8 }}>
            <div style={{ fontSize:13 }}>
              <b className="mono">{c.username}</b>
              {c.purpose && <span style={{ color:'var(--text2)' }}> · {c.purpose}</span>}
              <div style={{ fontSize:11.5, color:'var(--text2)' }}>
                {c.has_password ? 'password stored' : 'no password stored'}
                {c.last_revealed && ` · last viewed ${dt(c.last_revealed)}`}
              </div>
            </div>
            <div className="flex-gap" style={{ gap:8, alignItems:'center' }}>
              {shown[c.id]
                ? <span className="mono" style={{ fontSize:15, padding:'3px 8px', background:'var(--bg3)',
                    border:'1px solid var(--border)', borderRadius:5 }}>{shown[c.id]}</span>
                : <span className="mono" style={{ fontSize:14, color:'var(--text3)' }}>••••••••</span>}
              {c.has_password && server.can_reveal && !shown[c.id] && otpFor !== c.id &&
                <button className="btn btn-secondary" disabled={busy}
                        onClick={() => askCode(c.id)}>Email me a code</button>}
              {editable && <button className="btn btn-secondary" style={{ padding:'2px 8px', fontSize:11 }}
                onClick={async () => {
                  if (!window.confirm(`Delete the login "${c.username}"?`)) return;
                  await api.delete(`/servers/${id}/credentials/${c.id}`).catch(()=>{});
                  reload();
                }}>remove</button>}
            </div>
          </div>
          {otpFor === c.id && (
            <div className="flex-gap" style={{ gap:8, marginTop:8, alignItems:'center', flexWrap:'wrap' }}>
              <span style={{ fontSize:12.5, color:'var(--text2)' }}>Code sent to {sentTo} —</span>
              <input value={otp} onChange={e => setOtp(e.target.value.replace(/\D/g,'').slice(0,6))}
                     placeholder="000000" className="mono" style={{ width:110, letterSpacing:3 }} />
              <button className="btn btn-primary" disabled={busy || otp.length !== 6}
                      onClick={() => reveal(c.id)}>Show password</button>
              <button className="btn btn-secondary" onClick={() => { setOtpFor(null); setOtp(''); }}>Cancel</button>
            </div>
          )}
        </div>
      ))}

      {err && <div style={{ fontSize:12.5, color:'var(--red)', marginTop:8 }}>{err}</div>}

      {editable && (adding ? (
        <div style={{ marginTop:12, borderTop:'1px solid var(--border)', paddingTop:12 }}>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:10 }}>
            <div className="form-group" style={{ margin:0 }}><label>Username</label>
              <input value={form.username} onChange={e => setForm(f=>({...f,username:e.target.value}))} /></div>
            <div className="form-group" style={{ margin:0 }}><label>Purpose</label>
              <input value={form.purpose} onChange={e => setForm(f=>({...f,purpose:e.target.value}))}
                     placeholder="local admin, iDRAC…" /></div>
            <div className="form-group" style={{ margin:0 }}><label>Password</label>
              <input type="password" value={form.password} autoComplete="new-password"
                     onChange={e => setForm(f=>({...f,password:e.target.value}))} />
              <span style={{ fontSize:11, color:'var(--text2)' }}>Leave blank to keep the stored one.</span></div>
          </div>
          <div className="flex-gap" style={{ gap:8, marginTop:10 }}>
            <button className="btn btn-primary" disabled={busy || !form.username} onClick={save}>Save login</button>
            <button className="btn btn-secondary" onClick={() => { setAdding(false); setErr(''); }}>Cancel</button>
          </div>
        </div>
      ) : (
        <button className="btn btn-secondary" style={{ marginTop:10 }} onClick={() => setAdding(true)}>+ Add login</button>
      ))}
    </div>
  );
}
